import { useCMSPageField } from '@/cms/fields'
import { CheckoutMessage } from '@/scopes/checkout/components/CheckoutMessage'
import { ParticipantsBadgeForm } from '@/scopes/checkout/components/ParticipantsBadgeForm'
import { BridgeService } from '@/scopes/checkout/components/services/BridgeService'
import {
  CarRentalFilteringService,
} from '@/scopes/checkout/components/services/CarRentalFilteringService/CarRentalFilteringService'
import { DinnerVouchersService } from '@/scopes/checkout/components/services/DinnerVouchersService'
import { getGenericServiceComponent } from '@/scopes/checkout/components/services/getGenericServiceComponent'
import { GolfService } from '@/scopes/checkout/components/services/GolfService'
import { SkiPassService } from '@/scopes/checkout/components/services/SkiAncillariesService'
import { SkiLessonService } from '@/scopes/checkout/components/services/SkiAncillariesService/SkiLessonService'
import { SkiMaterialService } from '@/scopes/checkout/components/services/SkiAncillariesService/SkiMaterialService'
import { useCheckoutSid } from '@/scopes/checkout/domain/hooks'
import { ServiceTypeApi } from '@/scopes/checkout/infrastructure/api/types'
import { useFeatureFlag } from '@/scopes/common/infrastructure/feature-flags'
import { mqMin, tokPx } from '@sunwebgroup/ui-components'
import React from 'react'
import { styled } from 'styled-components'
import { BookingRedeemerService } from './BookingRedeemerService'
import { CancellationInsuranceService } from './CancellationInsuranceService'
import { CarRentalService } from './CarRentalService/CarRentalService'
import { LuggageService } from './LuggageService'

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  gap: ${tokPx('primitives_space_16')};

  @media ${mqMin('primitives_breakpoint_m_min')} {
    gap: ${tokPx('primitives_space_24')};
  }
`

export function Services() {
  const sid = useCheckoutSid()
  const showCarRentalFilteringService = useFeatureFlag('SHOW_CAR_RENTAL_FILTERING_SERVICE')
  const items = useCMSPageField('Checkout.items')?.items ?? []

  if (!sid || !items) {
    return null
  }

  return (
    <Wrapper>
      {items.map((item, index) => {
        const { type, componentId, aName } = item
        switch (type) {
          case ServiceTypeApi.ConditionalMessages:
            return <CheckoutMessage key={`${type}-${index}`} componentId={componentId} />
          case ServiceTypeApi.BookingRedeemer:
            return <BookingRedeemerService key={`${type}-${index}`} />
          case ServiceTypeApi.ParticipantsBadge:
            return <ParticipantsBadgeForm key={`${type}-${index}`} />
          case ServiceTypeApi.SkiAncillaries:
            return (
              <React.Fragment key={`${type}-${index}`}>
                <SkiPassService key={`SkiPass-${index}`} />
                <SkiMaterialService key={`SkiMaterial-${index}`} />
                <SkiLessonService key={`SkiLesson-${index}`} />
              </React.Fragment>
            )
          case ServiceTypeApi.Luggage:
            return <LuggageService key={`${type}-${index}`} {...item.serviceConfigurations} />
          case ServiceTypeApi.CarRental:
            return (
              <React.Fragment key={`${type}-${index}`}>
                {!showCarRentalFilteringService && <CarRentalService key={`${type}-${index}`} />}
                {showCarRentalFilteringService && <CarRentalFilteringService key={`${type}-${index}`} />}
              </React.Fragment>
            )
          case ServiceTypeApi.CancellationInsurance:
            return <CancellationInsuranceService key={`${type}-${index}`} item={item} />
          case ServiceTypeApi.Golf:
            return <GolfService key={`${type}-${index}`} />
          case ServiceTypeApi.GenericPerUnitComponent:
            if (aName === 'MEAL-VOUCHERS') {
              return <DinnerVouchersService key={`${type}-${index}`} />
            }
            return null
          case ServiceTypeApi.GenericPerParticipantSelection:
            if (aName === 'BRIDGE') {
              return <BridgeService key={`${type}-${index}`} />
            }
            return null
          case ServiceTypeApi.Generic:
            return getGenericServiceComponent(aName ?? '', item, type, index)
          default:
            if (window.newrelic) {
              const error = new Error(`[CheckoutSteps] No service ${type} found`)
              window.newrelic.noticeError(error)
            }
            return null
        }
      })}
    </Wrapper>
  )
}
